// src/pages/CustomerPackages.jsx
// Customer-facing service packages page. Shows all active packages assigned
// to this customer, with session icons indicating used vs available. Clicking
// an available session icon navigates to the booking page with the package
// pre-selected.
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { getSalonConfig } from '../api/salonConfigService';
import {
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import '../styles/loyalty.css';
import '../styles/wallet.css';

const getFontSizeValue = (fontSize) => {
  switch (fontSize) {
    case 'small': return '14px';
    case 'large': return '18px';
    default: return '16px';
  }
};

// Matches the wallet/loyalty icon style — a ticket/package icon
const PackagesIcon = ({ size = 64, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);

const SessionUsedIcon = ({ color }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" fill={color} fillOpacity="0.15"/>
    <polyline points="8 12 10.5 14.5 16 9"/>
  </svg>
);

const SessionAvailableIcon = ({ color }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);

function CustomerPackages() {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const { currentCustomer } = useCustomerAuth();

  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#5b21b6');

  // Load salon branding
  useEffect(() => {
    if (!salonId) return;
    getSalonConfig(salonId).then(cfg => {
      if (cfg?.branding) {
        const root = document.documentElement;
        if (cfg.branding.colors) {
          const primary = cfg.branding.colors.primary || '#5b21b6';
          setPrimaryColor(primary);
          root.style.setProperty('--salon-primary', primary);
          root.style.setProperty('--salon-secondary', cfg.branding.colors.secondary || '#ddd6fe');
          root.style.setProperty('--salon-accent', cfg.branding.colors.accent || '#F6F6F6');
          root.style.setProperty('--salon-background', cfg.branding.colors.background || '#FFFFFF');
          root.style.setProperty('--salon-text', cfg.branding.colors.text || '#333333');
        }
        if (cfg.branding.typography) {
          root.style.setProperty('--salon-heading-font', cfg.branding.typography.headingFont || 'Familjen Grotesk, sans-serif');
          root.style.setProperty('--salon-body-font', cfg.branding.typography.bodyFont || 'Wix Madefor Text, sans-serif');
          root.style.setProperty('--salon-font-size', getFontSizeValue(cfg.branding.typography.fontSize));
        }
      }
    }).catch(() => {});
  }, [salonId]);

  // Real-time listener on customerPackages collection
  useEffect(() => {
    if (!currentCustomer || !salonId) { setLoading(false); return; }
    const customerId = `${currentCustomer.email}_${salonId}`;

    // Filter only by customerId (already salon-scoped as email_salonId)
    // to avoid requiring a composite Firestore index.
    const q = query(
      collection(db, 'customerPackages'),
      where('customerId', '==', customerId)
    );

    const unsub = onSnapshot(q, snap => {
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(d => d.status === 'active');
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPackages(data);
      setLoading(false);
    }, err => {
      console.error('CustomerPackages listener error:', err);
      setError('Failed to load packages.');
      setLoading(false);
    });

    return unsub;
  }, [currentCustomer, salonId]);

  // Real-time listener on customerPackageHistory collection
  useEffect(() => {
    if (!currentCustomer || !salonId) return;
    const customerId = `${currentCustomer.email}_${salonId}`;
    const q = query(
      collection(db, 'customerPackageHistory'),
      where('customerId', '==', customerId)
    );
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.performedAt?.seconds || 0) - (a.performedAt?.seconds || 0));
      setHistory(data);
    }, err => {
      console.error('CustomerPackageHistory listener error:', err);
    });
    return unsub;
  }, [currentCustomer, salonId]);

  const handleAvailableSessionClick = useCallback((pkg) => {
    navigate(`/BespokeBeauty/${salonId}/customer/book`, {
      state: { preselectedPackage: { id: pkg.id, name: pkg.packageName } }
    });
  }, [navigate, salonId]);

  return (
    <div className="loyalty-page">
      <main className="ap-container">

        {error && (
          <div className="ap-toast ap-toast--error">
            <span>{error}</span>
            <button onClick={() => setError('')} aria-label="Dismiss">×</button>
          </div>
        )}

        {/* ── Header ── */}
        <div className="ap-hero-card">
          <div className="ap-hero-eyebrow">
            <span className="ap-hero-tier-badge">My Packages</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: '8px' }}>
            <PackagesIcon size={56} color="currentColor" />
          </div>
          <div className="ap-hero-top-tier">
            Your active service packages and remaining sessions
          </div>
        </div>

        {/* ── Package cards ── */}
        <section className="ap-section">
          <h2 className="ap-section-title">Active Packages</h2>

          {loading ? (
            <p style={{ color: '#6b7280', textAlign: 'center' }}>Loading packages…</p>
          ) : packages.length === 0 ? (
            <div className="ap-empty-state">
              <PackagesIcon size={48} color={primaryColor} />
              <p className="ap-empty-title">No active packages</p>
              <p className="ap-empty-sub">Your service packages will appear here once added by the salon.</p>
            </div>
          ) : (
            packages.map(pkg => {
              const total = pkg.totalSessions || 0;
              const used = pkg.sessionsUsed || 0;
              const left = Math.max(0, total - used);
              const sessions = Array.from({ length: total }, (_, i) => i < used ? 'used' : 'available');

              return (
                <div key={pkg.id} style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '16px',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--salon-primary, #5b21b6)' }}>
                      {pkg.packageName}
                    </h3>
                    <span style={{
                      background: 'var(--salon-secondary, #ddd6fe)',
                      color: 'var(--salon-primary, #5b21b6)',
                      fontSize: '12px', fontWeight: 600,
                      padding: '2px 10px', borderRadius: '12px'
                    }}>
                      {left} left
                    </span>
                  </div>
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                    {used} used · {left} available · {total} total
                  </p>

                  {/* Session icons */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {sessions.map((state, i) =>
                      state === 'used' ? (
                        <span key={i} title="Session used" style={{ opacity: 0.45 }}>
                          <SessionUsedIcon color={primaryColor} />
                        </span>
                      ) : (
                        <button
                          key={i}
                          title="Click to book this session"
                          onClick={() => handleAvailableSessionClick(pkg)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 0, display: 'flex', alignItems: 'center'
                          }}
                        >
                          <SessionAvailableIcon color={primaryColor} />
                        </button>
                      )
                    )}
                  </div>

                  <p style={{ margin: '12px 0 0', fontSize: '11px', color: '#9ca3af' }}>
                    Tap an available session icon to book
                  </p>
                </div>
              );
            })
          )}
        </section>

        {/* ── Package History ── */}
        <section className="ap-section">
          <h2 className="ap-section-title">Package History</h2>

          {history.length === 0 ? (
            <div className="ap-empty-state">
              <p className="ap-empty-sub">No package actions recorded yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {history.map(entry => {
                const actionColor =
                  entry.action === 'Add Package'     ? { bg: '#eff6ff', text: '#1d4ed8' } :
                  entry.action === 'Delete Package'  ? { bg: '#fef2f2', text: '#dc2626' } :
                  entry.action === 'Deplete Session' ? { bg: '#fff7ed', text: '#c2410c' } :
                                                       { bg: '#f0fdf4', text: '#15803d' };
                const date = entry.performedAt?.toDate
                  ? entry.performedAt.toDate().toLocaleString()
                  : '—';
                return (
                  <div key={entry.id} style={{
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    padding: '14px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{
                        background: actionColor.bg, color: actionColor.text,
                        fontSize: '11px', fontWeight: 700,
                        padding: '3px 10px', borderRadius: '12px', whiteSpace: 'nowrap',
                      }}>
                        {entry.action}
                      </span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                        {entry.packageName || '—'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {entry.sessionsUsedAfter ?? 0} used · {entry.sessionsLeftAfter ?? '—'} left · {entry.totalSessionsAfter ?? '—'} total
                      </span>
                      <span style={{ fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {date}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

      </main>
    </div>
  );
}

export default CustomerPackages;
